import { ShipRecordBytes, decodeShipRecord } from './ship_record.js'

export const ShipsPerPacket = 100
export const ViewscreenArrayBytes = ShipsPerPacket * ShipRecordBytes // 1000
export const PacketHeaderBytes = 20
export const ViewscreenPacketBytes = PacketHeaderBytes + ViewscreenArrayBytes // 1020

// World-unit scales used by the wire format. Must match backend/instance/physics.go.
export const PosWireScale = 4   // wire pos × (1/4) = world pos
export const VelWireScale = 10  // wire vel × (1/10) = world vel (per tick)

// PacketHeader shape:
//   {
//     serverTick:   number,
//     selfWorldX:   number,   // world units
//     selfWorldY:   number,
//     selfVelX:     number,   // world units per tick
//     selfVelY:     number,
//     selfRotation: number,   // radians
//     selfSprite:   number,   // raw sprite byte
//     selfShield:   number,   // 0..255
//   }

// decodeViewscreen takes an ArrayBuffer of exactly ViewscreenPacketBytes and
// returns { header, records } where records contains only the non-empty slots
// (viewscreen_id > 0). Returns null if the buffer is the wrong size.
export function decodeViewscreen(buf) {
  if (buf.byteLength !== ViewscreenPacketBytes) return null
  const view = new DataView(buf)
  const header = decodePacketHeader(view, 0)
  const records = []
  for (let i = 0; i < ShipsPerPacket; i++) {
    const rec = decodeShipRecord(view, PacketHeaderBytes + i * ShipRecordBytes)
    if (rec.viewscreenId === 0) continue
    records.push(rec)
  }
  return { header, records }
}

// encodePacketHeader writes a PacketHeader at `offset` within the DataView.
// Inverse of decodePacketHeader. `hdr` uses the same shape the decoder
// produces (world-unit floats / radians). Values outside representable
// ranges are truncated by the underlying DataView setters.
export function encodePacketHeader(view, offset, hdr) {
  view.setUint32(offset, hdr.serverTick >>> 0, true)
  view.setInt32(offset + 4, Math.round(hdr.selfWorldX * PosWireScale), true)
  view.setInt32(offset + 8, Math.round(hdr.selfWorldY * PosWireScale), true)
  view.setInt16(offset + 12, Math.round(hdr.selfVelX * VelWireScale), true)
  view.setInt16(offset + 14, Math.round(hdr.selfVelY * VelWireScale), true)
  const rotWire = Math.round((hdr.selfRotation / (2 * Math.PI)) * 65536) & 0xffff
  view.setUint16(offset + 16, rotWire, true)
  view.setUint8(offset + 18, hdr.selfSprite & 0xff)
  view.setUint8(offset + 19, hdr.selfShield & 0xff)
}

function decodePacketHeader(view, offset) {
  const serverTick = view.getUint32(offset, true)
  const posXWire = view.getInt32(offset + 4, true)
  const posYWire = view.getInt32(offset + 8, true)
  const velXWire = view.getInt16(offset + 12, true)
  const velYWire = view.getInt16(offset + 14, true)
  const rotWire = view.getUint16(offset + 16, true)
  const selfSprite = view.getUint8(offset + 18)
  const selfShield = view.getUint8(offset + 19)
  return {
    serverTick,
    selfWorldX: posXWire / PosWireScale,
    selfWorldY: posYWire / PosWireScale,
    selfVelX: velXWire / VelWireScale,
    selfVelY: velYWire / VelWireScale,
    selfRotation: (rotWire / 65536) * 2 * Math.PI,
    selfSprite,
    selfShield,
  }
}
